/**
 * Futures Data Context
 *
 * Provides real-time Binance Futures data (mark prices with best bid/ask) via React Context.
 * Manages BinanceFuturesClient lifecycle and syncs data to React state every 1 second.
 *
 * Data Sources:
 * - Mark prices: watchMarkPrices() - funding rates (1s updates)
 * - Best bid/ask: watchBidsAsks() - real-time book ticker prices
 *
 * Note: Orderbooks are NOT subscribed (per Epic 10 - fetched on-demand only)
 */

import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { BinanceFuturesClient } from '../services/futures/binance-futures-client';
import type { MarkPriceData } from '../services/futures/types';

/**
 * Sync interval for copying service Maps to React state
 * Matches mark price update frequency (1 second)
 */
const SYNC_INTERVAL_MS = 1000;

/**
 * Futures Data Context Interface
 */
interface FuturesDataContextType {
  // Data maps (synchronized from service)
  // Mark prices include bestBid/bestAsk from book ticker stream
  markPrices: Map<string, MarkPriceData>;

  // Connection state
  connected: boolean;
  loading: boolean;
  error: string | null;

  // Monitoring
  staleSymbolCount: number;
  lastUpdate: number;  // Timestamp of last sync
}

const FuturesDataContext = createContext<FuturesDataContextType | undefined>(undefined);

/**
 * Futures Data Provider Component
 *
 * Wraps BinanceFuturesClient and manages its lifecycle:
 * - Connects on mount, disconnects on unmount
 * - Syncs data every 1 second from client Maps to React state
 * - Filters out stale data (>30s old)
 * - Tracks connection errors and staleness count
 */
export const FuturesDataProvider: React.FC<{ children: ReactNode; enabled?: boolean }> = ({
  children,
  enabled = true
}) => {
  // State
  const [markPrices, setMarkPrices] = useState<Map<string, MarkPriceData>>(new Map());
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staleSymbolCount, setStaleSymbolCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(0);

  // Client reference (created once, never recreated)
  const clientRef = useRef<BinanceFuturesClient | null>(null);

  /**
   * Initialization Effect: Create client ONCE on mount
   * This ensures we only create ONE client instance that persists
   * across enabled/disabled state changes
   *
   * CRITICAL: Handles React Strict Mode properly
   * In Strict Mode, React intentionally double-mounts to detect side effects.
   * We create the client once and reuse it, WITHOUT disconnecting in cleanup.
   * The connection state is managed by the separate connection effect.
   */
  useEffect(() => {
    // Only create client if it doesn't already exist
    if (clientRef.current === null) {
      console.log('[FuturesDataContext] Creating Binance Futures client');
      clientRef.current = new BinanceFuturesClient();
    } else {
      console.log('[FuturesDataContext] Client already exists, reusing');
    }

    // NO cleanup needed - client persists across re-mounts
    // Connection/disconnection is handled by the separate connection effect
  }, []); // Empty deps = runs once on mount

  /**
   * Connection Effect: Connect/Disconnect based on enabled flag
   * This reuses the SAME client instance, just connects/disconnects
   */
  useEffect(() => {
    const client = clientRef.current;
    if (!client) {
      return; // Client not yet created
    }

    // If disabled, disconnect and clear data
    if (!enabled) {
      console.log('[FuturesDataContext] Futures feature disabled, disconnecting...');

      // Disconnect if currently connected
      if (client.isConnected()) {
        client.disconnect();
      }

      // Clear React state
      setMarkPrices(new Map());
      setConnected(false);
      setLoading(false);
      setError(null);
      return;
    }

    // If enabled and not already connected, connect
    if (enabled && !client.isConnected()) {
      console.log('[FuturesDataContext] Futures feature enabled, connecting...');

      const connectClient = async () => {
        try {
          setLoading(true);
          setError(null);

          console.log('[FuturesDataContext] Connecting to Binance Futures...');
          await client.connect();

          setConnected(true);
          setLoading(false);
          console.log('[FuturesDataContext] Connected successfully');

          // DEBUG: Log initial status
          const status = client.getStatus();
          console.log('[FuturesDataContext] Initial status:', status);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown connection error';
          console.error('[FuturesDataContext] Connection failed:', errorMessage, err);
          setError(errorMessage);
          setConnected(false);
          setLoading(false);
        }
      };

      // Start connection
      connectClient();
    }

    // No cleanup needed here - handled by initialization effect
  }, [enabled]);

  /**
   * Sync Effect: Copy Maps to React state every 1 second
   *
   * Why polling instead of direct Map access?
   * - React doesn't detect Map mutations (not reactive)
   * - Polling ensures React re-renders when data changes
   * - 1-second interval matches mark price update frequency
   */
  useEffect(() => {
    if (!connected || !clientRef.current) {
      return;
    }

    console.log('[FuturesDataContext] Starting data synchronization (1s interval)');

    let syncCount = 0; // Track number of syncs for periodic logging

    const syncInterval = setInterval(() => {
      const client = clientRef.current;
      if (!client) {
        return;
      }

      try {
        // Get fresh mark price data from client
        // Mark prices now include bestBid/bestAsk from book ticker stream
        const freshMarkPrices = client.getAllMarkPrices();

        // Convert array to Map for context
        // Client already normalizes symbols (removes ":USDT" suffix)
        const markPriceMap = new Map(
          freshMarkPrices.map(mp => [mp.symbol, mp])
        );

        // Count stale symbols (those filtered out by getAllMarkPrices)
        const status = client.getStatus();
        const totalMarkPrices = status.markPrices;
        const staleCount = totalMarkPrices - markPriceMap.size;

        // Update React state
        setMarkPrices(markPriceMap);
        setStaleSymbolCount(staleCount);
        setLastUpdate(Date.now());

        // Log sync data every 10 seconds (not every second)
        syncCount++;
        if (syncCount % 10 === 0) {
          console.log('[FuturesDataContext] Status:', {
            markPrices: markPriceMap.size,
            stale: staleCount,
          });

          // DEBUG: Show what symbols we actually have with bestBid data
          const availableSymbols = Array.from(markPriceMap.keys());
          const sampleData = availableSymbols.length > 0 ? markPriceMap.get(availableSymbols[0]) : null;
          console.log('[FuturesDataContext] Available futures symbols:', {
            total: availableSymbols.length,
            hasBTC: availableSymbols.includes('BTC/USDT'),
            hasETH: availableSymbols.includes('ETH/USDT'),
            first10: availableSymbols.slice(0, 10),
            sampleBestBid: sampleData?.bestBid,
            sampleFundingRate: sampleData?.fundingRate,
          });
        }
      } catch (err) {
        console.error('[FuturesDataContext] Sync error:', err);
      }
    }, SYNC_INTERVAL_MS);

    // Cleanup interval on unmount or disconnect
    return () => {
      console.log('[FuturesDataContext] Stopping data synchronization');
      clearInterval(syncInterval);
    };
  }, [connected]);

  const value: FuturesDataContextType = {
    markPrices,
    connected,
    loading,
    error,
    staleSymbolCount,
    lastUpdate,
  };

  return (
    <FuturesDataContext.Provider value={value}>
      {children}
    </FuturesDataContext.Provider>
  );
};

/**
 * Custom Hook: useFuturesData
 *
 * Returns full futures data context.
 *
 * @throws Error if used outside FuturesDataProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useFuturesData = () => {
  const context = useContext(FuturesDataContext);
  if (!context) {
    throw new Error('useFuturesData must be used within FuturesDataProvider');
  }
  return context;
};
